import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';

/**
 * Même garde-fou que DocumentPicker : borne un dépôt absurde, sans être une
 * contrainte serveur (multer n'a aucun `limits.fileSize`). Volontairement large,
 * un scan de bureau à 600 dpi pesant couramment 10 à 20 Mo.
 */
const TAILLE_MAX = 20 * 1024 * 1024;

const ACCEPT_PAR_DEFAUT = 'image/*,application/pdf';

interface Props {
  /** Intitulé affiché au-dessus de la zone ("Lettre d'invitation"). */
  label: string;
  /** Fichier courant : le composant est contrôlé par le formulaire. */
  file: File | null;
  /** Appelé avec le fichier retenu, ou null quand l'utilisateur le retire. */
  onFileSelected: (fichier: File | null) => void;
  /** Types acceptés, au format attribut `accept`. */
  accept?: string;
  /** Ajoute l'astérisque rouge à côté du label. */
  required?: boolean;
  /** Grise la zone et refuse toute sélection (cas d'une case « je fournirai plus tard »). */
  disabled?: boolean;
  /** Message d'erreur venant de la validation du formulaire. */
  erreur?: string;
  /** Précision affichée à côté de l'intitulé (ex. "Datée de moins de 3 mois"). */
  aide?: string;
}

const formaterTaille = (octets: number): string => {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
};

/**
 * Sélecteur de fichier moderne : rectangle en pointillés, glisser-déposer, puis
 * vignette du document déposé. Remplace les `<input type="file">` bruts des
 * formulaires pays, qui n'offraient ni retrait du fichier, ni contrôle de
 * taille ou de format, et dont l'aperçu PDF occupait 500 px de haut.
 */
const FileDropZone: React.FC<Props> = ({
  label,
  file,
  onFileSelected,
  accept = ACCEPT_PAR_DEFAUT,
  required = false,
  disabled = false,
  erreur,
  aide,
}) => {
  const theme = useTheme();
  const surMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [survol, setSurvol] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [apercu, setApercu] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // dragenter/dragleave se déclenchent aussi au passage sur les enfants de la
  // zone : sans compteur, le cadre clignote dès que le curseur survole le texte.
  const compteurDrag = useRef(0);

  // La validation suit l'attribut `accept` du formulaire appelant, pour ne jamais
  // refuser un fichier que la boîte de dialogue vient d'autoriser à choisir.
  const pdfAutorise = accept.includes('pdf');
  const toutesImages = accept.includes('image/*');

  // L'URL d'aperçu suit le fichier : révoquée dès qu'il change ou disparaît.
  useEffect(() => {
    if (!file) {
      setApercu(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const estPdf = file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '');

  const formatsLisibles = toutesImages
    ? pdfAutorise
      ? 'Une image ou un PDF'
      : 'Une image'
    : pdfAutorise
    ? 'Une image JPEG ou PNG, ou un PDF'
    : 'Une image JPEG ou PNG';

  const validerEtRetenir = (fichier: File) => {
    if (fichier.size > TAILLE_MAX) {
      setRefus(`Fichier trop volumineux (${formaterTaille(fichier.size)}). La limite est de 20 Mo.`);
      return;
    }

    // Type DÉCLARÉ par le navigateur, pas les octets réels : cela écarte les
    // erreurs de sélection, pas un fichier malveillant.
    const estImage = toutesImages
      ? fichier.type.startsWith('image/')
      : ['image/jpeg', 'image/png'].includes(fichier.type);
    const extensionsAcceptees = pdfAutorise ? /\.(jpe?g|png|pdf)$/i : /\.(jpe?g|png)$/i;
    const typeAccepte =
      estImage ||
      (pdfAutorise && fichier.type === 'application/pdf') ||
      // Certains systèmes ne déclarent aucun type : on retombe sur l'extension.
      (fichier.type === '' && extensionsAcceptees.test(fichier.name));

    if (!typeAccepte) {
      setRefus(`Format non accepté. Utilisez ${formatsLisibles.toLowerCase()}.`);
      return;
    }

    setRefus(null);
    onFileSelected(fichier);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fichier = e.target.files && e.target.files[0];
    // Permet de resélectionner le même fichier après un refus ou un retrait.
    e.target.value = '';
    if (fichier) validerEtRetenir(fichier);
  };

  // Zone désactivée : aucun gestionnaire, le dépôt est simplement ignoré.
  const gestionnairesDrag = disabled
    ? {}
    : {
        onDragEnter: (e: React.DragEvent) => {
          e.preventDefault();
          compteurDrag.current += 1;
          setSurvol(true);
        },
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: (e: React.DragEvent) => {
          e.preventDefault();
          compteurDrag.current -= 1;
          if (compteurDrag.current <= 0) {
            compteurDrag.current = 0;
            setSurvol(false);
          }
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          compteurDrag.current = 0;
          setSurvol(false);
          const fichier = e.dataTransfer.files && e.dataTransfer.files[0];
          if (fichier) validerEtRetenir(fichier);
        },
      };

  const message = refus ?? erreur;
  const enErreur = Boolean(message);

  const entete = (
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mb: 0.75, color: disabled ? 'text.disabled' : 'text.primary' }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
          *
        </Box>
      )}
      {aide && (
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
          {aide}
        </Typography>
      )}
    </Typography>
  );

  const inputCache = (
    <Box
      component="input"
      ref={inputRef}
      type="file"
      accept={accept}
      disabled={disabled}
      onChange={handleInputChange}
      sx={{
        // Hors flux mais focusable au clavier (un `display: none` ne l'est pas),
        // ce qui permet à la zone de porter l'anneau de focus via :focus-within.
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
      }}
    />
  );

  // Fichier déposé : la vignette remplace la zone, qui reste une cible de dépôt
  // pour permettre le remplacement sans passer par le bouton.
  if (file) {
    return (
      <Box sx={{ my: 2 }}>
        {entete}
        <Box
          {...gestionnairesDrag}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            borderRadius: 3,
            border: '1px solid',
            borderColor: survol ? 'primary.main' : alpha(theme.palette.success.main, 0.4),
            backgroundColor: survol
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.success.main, 0.06),
            transition: 'all 0.2s ease',
          }}
        >
          {inputCache}

          <Box
            component={apercu ? 'a' : 'div'}
            href={apercu || undefined}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              backgroundImage: !estPdf && apercu ? `url(${apercu})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              cursor: 'pointer',
            }}
          >
            {estPdf && <PictureAsPdfRoundedIcon sx={{ color: 'error.main' }} />}
          </Box>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {file.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formaterTaille(file.size)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            {apercu && (
              <Tooltip title="Voir le document">
                <IconButton size="small" component="a" href={apercu} target="_blank" rel="noopener noreferrer">
                  <OpenInNewRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Remplacer">
              <IconButton size="small" onClick={() => inputRef.current?.click()}>
                <AutorenewRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Retirer">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setRefus(null);
                  onFileSelected(null);
                }}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {message && (
          <Typography variant="caption" sx={{ color: 'error.main', mt: 0.75, ml: 1.5, display: 'block' }}>
            {message}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ my: 2 }}>
      {entete}
      <Box
        component="label"
        {...gestionnairesDrag}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 2,
          py: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: '2px dashed',
          borderColor: enErreur ? 'error.main' : survol ? 'primary.main' : 'divider',
          backgroundColor: survol
            ? alpha(theme.palette.primary.main, 0.08)
            : enErreur
            ? alpha(theme.palette.error.main, 0.04)
            : 'background.paper',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          textAlign: 'center',
          transition: 'all 0.2s ease',
          '&:hover': disabled
            ? undefined
            : {
                borderColor: enErreur ? 'error.main' : 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }}
      >
        {inputCache}

        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.primary.main, survol ? 0.18 : 0.1),
            color: 'primary.main',
            transition: 'all 0.2s ease',
            transform: survol ? 'scale(1.1)' : 'none',
          }}
        >
          <AddRoundedIcon fontSize="large" />
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {surMobile ? 'Appuyez pour choisir un fichier' : 'Glissez-déposez votre fichier ici'}
        </Typography>
        {!surMobile && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            ou{' '}
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
              cliquez pour parcourir
            </Box>
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {formatsLisibles} &middot; 20 Mo maximum
        </Typography>
      </Box>

      {message && (
        <Typography variant="caption" sx={{ color: 'error.main', mt: 0.75, ml: 1.5, display: 'block' }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default FileDropZone;
